/**
 * Menu-plan list page wrapper (issue #156 sub-PR #4).
 *
 * Wraps the generic `ResourcePage` so the M2 module can add a
 * "+ New plan" CTA that opens its own create dialog (see
 * `CreateMenuPlanDialog` — the generic create form is bypassed for
 * the reasons documented there: bespoke validation, week-collision
 * UX, navigate-to-detail-on-success).
 *
 * The CTA is permission-gated by `menu_plans:write` and only shows
 * when the operator can actually create. The trip-wire in
 * `__tests__/resource-shape.test.ts` continues to assert
 * `menuPlanResource.api.create` is undefined — the trip-wire's
 * intent (force a sub-PR author to wire a dedicated dialog) is met
 * by this wrapper, not by flipping the resource schema. Keeping
 * `api.create` undefined ALSO prevents the generic
 * `ResourceFormDialog` from rendering its "+ New" button —
 * otherwise we'd have two create buttons on the same page.
 */
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Plus } from 'lucide-react';
import { ResourcePage, Button } from '@resource-kit/ui/react';
import { useBetterAuth } from '@hooks/useAuth';
import { PERMISSIONS } from '@/permissions';
import { menuPlanResource } from '../schema';
import { CreateMenuPlanDialog } from '../components/CreateMenuPlanDialog';

export function MenuPlanListPage() {
  const { t } = useTranslation();
  const { user } = useBetterAuth();
  const [createOpen, setCreateOpen] = useState(false);

  // Create re-gated onto `catalog:write` — `menu_plans:write` folded
  // into the unified `catalog:*` module in the backend redesign (#943).
  const canCreate = user?.permissions?.includes(PERMISSIONS.CATALOG_WRITE) ?? false;

  return (
    <>
      <ResourcePage
        bundle={menuPlanResource}
        // Route the bespoke create CTA through the PageHeader's actions region
        // instead of a `flex justify-end` div ABOVE ResourcePage, which floated
        // the button over the page title (#dispatch-jobs-toolbar).
        headerActions={
          canCreate ? (
            <Button onClick={() => setCreateOpen(true)} data-testid="menu-plan-create-open">
              <Plus className="mr-1 h-4 w-4" />
              {t('menu_plan.create.openButton')}
            </Button>
          ) : undefined
        }
      />
      <CreateMenuPlanDialog open={createOpen} onClose={() => setCreateOpen(false)} />
    </>
  );
}
